/**
 * Frame for loading day over day provincial / national COVID stats charts
 */

// import libs
import { select, selectAll } from 'd3-selection'
import { scaleTime } from 'd3-scale'
import { axisLeft, axisBottom } from 'd3-axis'
import { timeFormat } from 'd3-time-format'

// import chart renderers
import * as charts from './chart-renderers'

const ChartFrame = {
	data: null,
	width: null,
	height: 300,
	chartType: null,
	dataType: null,
	typeFormatted: null,
	showTests: false,
	logScale: false,
	highlights: false,
	yAxisLimit: {
		upper: 0,
		lower: 0
	},
	margin: {
		top: 10,
		right: 0,
		bottom: 30,
		left: 0
	},
	colors: {
		cases: '#497BA5',
		deaths: '#D91A4D',
		recov: '#ED9B40'
	},
	chart: {
		width: null,
		height: null,
		svg: null,
		elem: null,
		xScale: null,
		timeFormatter: null,
		histScale: null,
		tooltip: null
	},

	init( settings ) {
		// Initialize container
		this.$chart = document.querySelector( '.l-interactive__chart' )
		this.$tip = document.querySelector( '.l-interactive__tooltip' )

		// Initialize settings
		this.data             = settings.data
		this.chartType        = settings.chartType
		this.dataType         = settings.dataType
		this.typeFormatted    = settings.typeFormatted
		this.showTests        = settings.showTests
		this.logScale         = settings.logScale
		this.yAxisLimit.lower = settings.yAxisLimit
		this.highlights       = settings.highlights

		// Time formatter
		this.chart.timeFormatter = timeFormat( '%b %d' )

		// Initialize svg
		this.chart.svg = select( '.l-interactive__chart' )
			.attr( 'width', '100%' )
			.attr( 'height', this.height )
			.attr( 'preserveAspectRatio', 'xMidYMin meet')

		// Initialize chart
		this.chart.elem = this.chart.svg.append( 'g' )
			.attr( 'class', 'c-chart__inner' )

		// Initialize tooltip
		this.chart.tooltip = select( '.l-interactive__tooltip' )
		
		// Initialize Y axis
		this.chart.elem.append( 'g' )
			.attr( 'class', 'c-chart__axis c-chart__axis--y')

		// Initialize X axis
		this.chart.xScale = scaleTime()

		// Initialize X axis items
		this.chart.elem.append( 'g' )
			.attr( 'class', 'c-chart__axis c-chart__axis--x')

		// Initialize Y axis grid
		this.chart.elem.append( 'g' )
			.attr( 'class', 'c-chart__grid' )

		// Render chart
		this.render()
	},

	render() {
		charts[this.chartType].init( this.data, this.dataType, this.showTests, this.logScale )

		// Set dimensions
		this.width = this.$chart.clientWidth
		this.chart.width = this.width - ( this.margin.left + this.margin.right )
		this.chart.height = this.height - ( this.margin.top + this.margin.bottom )

		// SVG
		this.chart.svg.attr( 'viewBox', `0 0 ${this.width} ${this.height}` )

		// Chart
		this.chart.svg.select( '.c-chart__inner' )
			.attr( 'transform', `translate(${this.margin.left}, ${this.margin.top})` )

		// Y axis
		const yAxisUpperLimit = Math.max( this.yAxisLimit.lower, charts[this.chartType].bounds.maxY )
		const yAxisLowerLimit = charts[this.chartType].bounds.minY
		charts[this.chartType].yScale.range([this.chart.height, this.margin.top])
			.domain([yAxisLowerLimit, yAxisUpperLimit])

		if ( charts[this.chartType].yScaleValues ) {
			this.chart.elem.select( '.c-chart__axis--y' )
				.call( axisLeft( charts[this.chartType].yScale )
							.ticks( charts[this.chartType].yScaleTicks )
							.tickValues( charts[this.chartType].yScaleValues )
							.tickSize( 0 )
							.tickPadding( 12 ))
				.attr( 'text-anchor', 'start' )
				.attr( 'transform', 'translate(12, -12)' )
		} else {
			this.chart.elem.select( '.c-chart__axis--y' )
				.call( axisLeft( charts[this.chartType].yScale )
							.ticks( charts[this.chartType].yScaleTicks )
							.tickSize( 0 )
							.tickPadding( 12 ))
				.attr( 'text-anchor', 'start' )
				.attr( 'transform', 'translate(12, -12)' )
		}

		// X axis
		this.chart.xScale.range([0, this.chart.width])
			.domain([ charts[this.chartType].bounds.minX, charts[this.chartType].bounds.maxX ])

		this.chart.elem.select( '.c-chart__axis--x')
			.attr( 'transform', `translate(-5, ${this.chart.height})` )
			.call( axisBottom( this.chart.xScale )
				.tickValues( this.calculateXTicks( 5 ) )
				.tickSize( 0 )
				.tickPadding( 12 )
				.tickFormat( this.chart.timeFormatter ) )

		// Y axis grid
		if ( charts[this.chartType].yScaleValues ) {
			this.chart.elem.select( '.c-chart__grid')
				.call( axisLeft( charts[this.chartType].yScale )
					.ticks( charts[this.chartType].yScaleTicks )
					.tickValues( charts[this.chartType].yScaleValues )
					.tickFormat('')
					.tickSize( -this.chart.width ) )
				.selectAll( 'line' )
					.style( 'stroke', '#EDEDF0' )
		} else {
			this.chart.elem.select( '.c-chart__grid')
				.call( axisLeft( charts[this.chartType].yScale )
					.ticks( charts[this.chartType].yScaleTicks )
					.tickFormat('')
					.tickSize( -this.chart.width ) )
				.selectAll( 'line' )
					.style( 'stroke', '#EDEDF0' )
		}

		// Render chart content
		charts[this.chartType].render( this )

		// Render reopening bar
		this.reopeningBar()
	},

	updateChartType( chartType ) {
		this.chartType = chartType
	},

	calculateXTicks( maxTicks ) {
		const tickData = [...this.data]
		const tickInterval = Math.round( tickData.length / maxTicks )
		const tickItems = tickData.reverse().filter( ( item, i ) => 0 === ( i - 3 ) % tickInterval )
		return tickItems.reverse().map( item => new Date( item.date ) )
	},

	reopeningBar() {
		this.highlights.forEach( ( highlight ) => {
			if ( false !== highlight.date ) {
				const xPos = this.chart.xScale( highlight.date )
				const yPos = charts[this.chartType].yScale( charts[this.chartType].bounds.maxY * 0.8 )
				const yLim = charts[this.chartType].yScale( charts[this.chartType].bounds.minY )

				let shadowGroup = this.chart.elem.append( 'text' )
					.attr( 'class', 'c-chart__label c-chart__label--shadow c-chart__content' )
					.attr( 'text-anchor', 'middle' )
					.attr( 'transform', `translate(${xPos},${yPos - 35})`)

				shadowGroup.append( 'tspan' )
					.attr( 'class', 'c-chart__strong' )
					.text( this.chart.timeFormatter( highlight.date ) )
					
				shadowGroup.append( 'tspan' )
					.attr( 'x', 0 )
					.attr( 'dy', 14)
					.text( highlight.label1 )

				shadowGroup.append( 'tspan' )
					.attr( 'x', 0 )
					.attr( 'dy', 14)
					.text( highlight.label2 )

				let labelGroup = this.chart.elem.append( 'text' )
					.attr( 'class', 'c-chart__label c-chart__content' )
					.attr( 'text-anchor', 'middle' )
					.attr( 'fill', '#767676' )
					.attr( 'transform', `translate(${xPos},${yPos - 35})`)

				labelGroup.append( 'tspan' )
					.attr( 'class', 'c-chart__strong' )
					.text( this.chart.timeFormatter( highlight.date ) )
					
				labelGroup.append( 'tspan' )
					.attr( 'x', 0 )
					.attr( 'dy', 14)
					.text( highlight.label1 )

				labelGroup.append( 'tspan' )
					.attr( 'x', 0 )
					.attr( 'dy', 14)
					.text( highlight.label2 )

				let labelPointer = this.chart.elem.append( 'path' )
					.attr( 'class', 'c-chart__content' )
					.attr( 'stroke', '#767676' )
					.attr( 'stroke-width', '1.5' )
					.attr( 'd', `M${xPos} ${yPos}, L${xPos} ${yLim}`)
			}
		})
	}
}

export default ChartFrame
